import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FinancialProductFiltersContainer from './Filters/FinancialProductFiltersContainer';
import FinancialProductRulesContainer from './Rules/FinancialProductRulesContainer';
import FinancialProductPropertiesContainer from './Properties/FinancialProductPropertiesContainer';
import FinancialProductDetailsInsurersContainer from './Insurers/FinancialProductInsurersContainer';
import FinancialProductResidualGridsContainer from './ResidualGrids/FinancialProductResidualGridsContainer';
import FinancialProductDetailsPaymentPlansContainer from './PaymentPlans/FinancialProductDetailsPaymentPlansContainer';
import FinancialProductWarrantiesContainer from './Warranties/FinancialProductWarrantiesContainer';
import FinancialProductSeasonalityTemplatesContainer from './SeasonalityTemplates/FinancialProductSeasonalityTemplatesContainer';
import { NavPillsCardContainer } from 'components/common/Styles';
import useHistoryWithContext from 'components/menu/useHistoryWithContext';
import NavPillsWithPrompt from 'features/common/dirtyInfo/components/NavPillsWithPrompt';

const FinancialProductDetailsComponent = ({ canWrite, data, productId, isSaved, generalDataLens }) => {
  const [t] = useTranslation();
  const [visibleTabs, setVisibleTabs] = useState([]);
  const historyContext = useHistoryWithContext();
  const [active, setActive] = useState(0);

  useEffect(() => {
    switch (historyContext.location?.state?.from) {
      case 'Rules':
        setActive(1);
        break;
      case 'Warranties':
        setActive(4);
        break;
      default:
        setActive(0);
    }
  }, [historyContext.location?.state?.from]);

  const initialTabItems = useMemo(
    () => [
      {
        tab: {
          label: t('FinancialProduct.Details.Filters'),
          content: (
            <FinancialProductFiltersContainer
              productId={productId}
              phrase={data?.leasingFinancialProduct?.filterCondition}
              conditions={data?.financialProductFilters}
              canWrite={canWrite}
            />
          )
        },
        visible: true
      },
      {
        tab: {
          label: t('FinancialProduct.Details.Rules.Title'),
          content: (
            <FinancialProductRulesContainer
              rules={data?.financialProductRules ?? []}
              isSaved={isSaved}
              canWrite={canWrite}
              generalDataLens={generalDataLens}
            />
          )
        },
        visible: true
      },
      {
        tab: {
          label: t('FinancialProduct.Details.Proprieties'),
          content: (
            <FinancialProductPropertiesContainer
              productId={productId}
              properties={data?.financialProductProperties ?? []}
              isNewFinancialProduct={isSaved}
              canWrite={canWrite}
            />
          )
        },
        visible: true
      },
      {
        tab: {
          label: t('FinancialProduct.Details.Insurers.Title'),
          content: (
            <FinancialProductDetailsInsurersContainer
              isNewFinancialProduct={isSaved}
              canWrite={canWrite}
              productId={productId}
              productInsurerList={data?.financialProductInsurers ?? []}
            />
          )
        },
        visible: true
      },
      {
        tab: {
          label: t('FinancialProduct.Details.ResidualGrids'),
          content: (
            <FinancialProductResidualGridsContainer
              isNewFinancialProduct={isSaved}
              canWrite={canWrite}
              productId={productId}
              residualGrids={data?.financialProductResidualValueGrids ?? []}
            />
          )
        },
        visible: true
      },
      {
        tab: {
          label: t('FinancialProduct.Details.PaymentPlans'),
          content: <FinancialProductDetailsPaymentPlansContainer isSaved={isSaved} />
        },
        visible: false
      },
      {
        tab: {
          label: t('FinancialProduct.Details.Warranties.Title'),
          content: (
            <FinancialProductWarrantiesContainer
              warranties={data?.financialProductWarranties ?? []}
              isSaved={isSaved}
              canWrite={canWrite}
            />
          )
        },
        visible: true
      },
      {
        tab: {
          label: t('FinancialProduct.Details.SeasonalityTemplates.Title'),
          content: (
            <FinancialProductSeasonalityTemplatesContainer
              seasonalityTemplates={data?.financialProductSeasonalityTemplates ?? []}
              isSaved={isSaved}
              canWrite={canWrite}
              generalDataLens={generalDataLens}
            />
          )
        },
        visible: true
      }
    ],
    [data, productId, isSaved, canWrite, generalDataLens, t]
  );

  useEffect(() => {
    const items = initialTabItems
      .filter(item => item.visible)
      .map(item => {
        return { ...item.tab };
      });
    setVisibleTabs(items);
  }, [initialTabItems]);

  return (
    <NavPillsCardContainer disablePadding>
      <NavPillsWithPrompt
        fullWidth
        tabs={visibleTabs}
        active={active}
        onChange={(_event, active) => {
          setActive(active);
        }}
      />
    </NavPillsCardContainer>
  );
};

FinancialProductDetailsComponent.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  data: PropTypes.object,
  productId: PropTypes.number.isRequired,
  isSaved: PropTypes.bool.isRequired,
  generalDataLens: PropTypes.object.isRequired
};

export default FinancialProductDetailsComponent;
